"use client";

import {
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
    type CSSProperties,
} from "react";
import { getStrapiMedia } from "@/utils/api-helpers";
import Image from "next/image";
import BottomCurves from "../curves/bottomCurves";
import RevealElement from "../revealElement";
import BannerFloatingArrow from "../uiDecor/bannerFloatingArrow";

export default function Brands({ data }: any) {
    // SG marquee only when this specific block is tagged v2 (shared.version
    // component, opt-in per block) — anything else (field not set, older
    // content, MY's Strapi instance) falls back to the MY grid. Deciding
    // from the block's own payload, not the route, is what
    // lets Brands render correctly regardless of which renderer/page called
    // it (see utils/section-renderer.tsx and utils/sg-section-renderer.tsx).
    const isSG = data?.version?.version === "v2";
    const singleCopyRef = useRef<HTMLDivElement>(null);
    // Marks the first repeat-unit of the SECOND half — its offsetLeft is
    // exactly the pixel distance the strip must travel for the loop to be
    // seamless (see shiftPx below).
    const secondHalfRef = useRef<HTMLDivElement>(null);
    // Number of times the full brand list repeats within ONE half of the
    // strip. The strip is always rendered as two identical halves
    // (repeats copies each) and animated as a single unit — since the
    // halves are identical, sliding by exactly one half-width is
    // indistinguishable from the start, so the loop is seamless. This
    // replaces an earlier version that animated each copy independently,
    // which caused visible jolts/gaps: newly mounted copies start their own
    // CSS animation clock from 0, so they'd drift out of phase with copies
    // already running.
    const [repeats, setRepeats] = useState(1);
    // The exact pixel shift for a seamless loop. This is NOT the same as
    // 50% of the strip's total width: CSS `gap` only inserts spacing
    // *between* items (N-1 gaps for N items), so 50% of a gapped strip's
    // width under-shoots the true halfway point by half a gap every time —
    // a drift that was causing a visible snap once per loop, right as the
    // first logo hit the left edge. Measuring the real DOM offset instead
    // (offsetLeft ignores the currently-running transform) sidesteps that
    // arithmetic entirely.
    const [shiftPx, setShiftPx] = useState(0);

    // Each half must span at least the viewport width, at every point in the
    // slide cycle, or the right side goes blank while the sliding copy
    // catches up. Measure the real width of one repeat unit and use however
    // many repeats are actually needed (min N where
    // N >= ceil(viewportWidth / singleCopyWidth)), so it never gaps
    // regardless of screen size or brand-logo count.
    useEffect(() => {
        if (!isSG) return;

        const recalc = () => {
            const singleWidth = singleCopyRef.current?.offsetWidth || 0;
            if (!singleWidth) return;
            const needed = Math.ceil(window.innerWidth / singleWidth);
            setRepeats(Math.max(1, needed));
        };

        recalc();
        window.addEventListener("resize", recalc);
        return () => window.removeEventListener("resize", recalc);
    }, [isSG, data.brandLogos]);

    // Re-measure the true halfway offset whenever the DOM structure could
    // have changed it (repeats count, or the logos themselves).
    useLayoutEffect(() => {
        if (!isSG) return;
        setShiftPx(secondHalfRef.current?.offsetLeft || 0);
    }, [isSG, repeats, data.brandLogos]);

    // Keep the visual speed (px/sec) constant regardless of how many
    // repeats are needed: the animation travels shiftPx over this duration,
    // and shiftPx scales with repeats, so duration must too.
    const marqueeDurationSeconds = 32 * repeats;

    return (
        <section
            className={` ${
                isSG ? "pt-10 md:pt-16 px-8" : "bg-lightGrey pt-20 pb-36 px-8"
            }`}
        >
            <div className="flex flex-col items-center justify-center">
                <h2 className="text-darkGrey text-center text-font-100 ">
                    {data.heading}
                </h2>
                <RevealElement>
                    {isSG ? (
                        // SG: infinite sliding marquee. A single flex strip,
                        // rendered as two identical halves and animated as
                        // one unit (see repeats/marqueeDurationSeconds
                        // above) so the loop point and every logo-to-logo
                        // gap use the exact same gap-x value — nothing here
                        // relies on separately-timed animations lining up.
                        <div className="relative w-screen left-1/2 -translate-x-1/2 overflow-hidden">
                            <div
                                className="relative flex items-center gap-x-6 md:gap-x-10 w-max animate-brandMarquee"
                                style={
                                    {
                                        animationDuration: `${marqueeDurationSeconds}s`,
                                        "--brand-shift": `${shiftPx}px`,
                                    } as CSSProperties
                                }
                            >
                                {[...Array(repeats * 2)].map((_, i) => (
                                    <div
                                        key={i}
                                        ref={
                                            i === 0
                                                ? singleCopyRef
                                                : i === repeats
                                                  ? secondHalfRef
                                                  : undefined
                                        }
                                        className="flex items-center gap-x-6 md:gap-x-10 shrink-0"
                                    >
                                        {data.brandLogos.map(
                                            (brand: any, index: number) =>
                                                brand.image ? (
                                                    <div
                                                        key={`${i}-${index}`}
                                                        className="bg-white rounded-2xl px-6 py-3 flex items-center justify-center shrink-0 w-40 md:w-60"
                                                    >
                                                        <Image
                                                            src={getStrapiMedia(
                                                                brand.image.url,
                                                            )}
                                                            alt={
                                                                brand.image
                                                                    .name || ""
                                                            }
                                                            height={40}
                                                            width={100}
                                                            className="h-16 md:h-24 w-full object-contain grayscale md:hover:grayscale-0 transition duration-500 ease-in-out"
                                                            loading="eager"
                                                        />
                                                    </div>
                                                ) : null,
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        // MY: original static grid
                        <div className="px-4 py-4 md:py-8 grid grid-cols-5 md:grid-cols-8 gap-1 md:px-0 md:gap-3">
                            {data.brandLogos.map(
                                (brand: any, index: number) => {
                                    return brand.image ? (
                                        <Image
                                            key={index}
                                            src={getStrapiMedia(
                                                brand.image.url,
                                            )}
                                            alt={brand.image.name || ""}
                                            height={70}
                                            width={150}
                                            className="col-span-1 grayscale md:hover:grayscale-0 transition duration-500 ease-in-out"
                                            loading="lazy"
                                        />
                                    ) : (
                                        <div
                                            className="col-span-5 md:col-span-1"
                                            key={index}
                                        ></div>
                                    );
                                },
                            )}
                        </div>
                    )}
                </RevealElement>
            </div>
            {data.waveBgColor && (
                <BottomCurves
                    color={data.waveBgColor.waveBgColor}
                    direction="right"
                />
            )}
            {!isSG && (
                <BannerFloatingArrow
                    direction={"right"}
                    className={`right-[6vw] -top-20 md:-top-20`}
                    name="clients"
                />
            )}
        </section>
    );
}
